import { UtilityMethods } from "@mindmemobile/mm-shared-global";
import { BillingMethodType, InvoiceStatus } from "../enums";
import {
  IAPIInvoiceDetailModel,
  IAPIInvoiceDetailPageModel,
} from "../interfaces";
import { ContactReferenceModel } from "./ContactDetailPageModel";
import { MerchantAccount } from "./MerchantApplicationInputModel";
import { OrderRelatedGridModel } from "./OrderRelatedGridModels";
import { PaymentMethodModel } from "./PaymentMethodModels";
import { RelatedPaymentMethodGridModel } from "./RelatedPaymentMethodGridModel";
import { RelatedPaymentsGridModel } from "./RelatedPaymentsGridModel";
import { RelatedSubscriptionGridModel } from "./RelatedSubscriptionGridModel";

export class InvoiceDetailPageModel {
  detail: InvoiceDetailModel;
  payments: RelatedPaymentsGridModel[];
  orders: OrderRelatedGridModel[];
  subscriptions: RelatedSubscriptionGridModel[];
  lastPayment?: RelatedPaymentsGridModel;
  paymentMethods: RelatedPaymentMethodGridModel[];
  totalInvoices: number;
  previousRecordId: string;
  nextRecordId: string;
  index: number;
  constructor(data: Partial<InvoiceDetailPageModel> = {}) {
    this.detail = data.detail || new InvoiceDetailModel();
    this.payments =
      data.payments?.map((item) => new RelatedPaymentsGridModel(item)) || [];
    this.orders = data.orders || [];
    this.subscriptions =
      data.subscriptions?.map(
        (item) => new RelatedSubscriptionGridModel(item)
      ) || [];
    this.lastPayment =
      data.lastPayment && new RelatedPaymentsGridModel(data.lastPayment);
    this.paymentMethods =
      data.paymentMethods?.map(
        (item) => new RelatedPaymentMethodGridModel(item)
      ) || [];
    this.totalInvoices = data.totalInvoices || 0;
    this.previousRecordId = data.previousRecordId || "";
    this.nextRecordId = data.nextRecordId || "";
    this.index = data.index || 0;
  }

  static deserializeAPIModel(
    data: IAPIInvoiceDetailPageModel
  ): InvoiceDetailPageModel {
    const subscriptions =
      data.subscriptions &&
      data.subscriptions.map((item) =>
        RelatedSubscriptionGridModel.deserializeAPIModel(item)
      );
    const mappedData: Partial<InvoiceDetailPageModel> = {
      detail: InvoiceDetailModel.deserializeAPIModel(data.detail),
      subscriptions: subscriptions || [],
      payments:
        data.payments?.map((item) =>
          RelatedPaymentsGridModel.deserializeAPIModel(item)
        ) || [],
      orders:
        data.orders?.map((item) =>
          OrderRelatedGridModel.deserializeAPIModel(item)
        ) || [],
      lastPayment:
        data.lastPayment &&
        RelatedPaymentsGridModel.deserializeAPIModel(data.lastPayment),
      paymentMethods:
        data.paymentMethods?.map((item) =>
          RelatedPaymentMethodGridModel.deserializeAPIModel(item)
        ) || [],
      totalInvoices: data.total,
      previousRecordId: data.previousRecordId,
      nextRecordId: data.nextRecordId,
      index: data.index,
    };
    return new InvoiceDetailPageModel(mappedData);
  }
}

export class InvoiceDetailModel {
  id: string;
  createdDate: Date;
  status: keyof typeof InvoiceStatus;
  pastDueDays: number;
  invoiceNumber: string;
  description: string;
  amount: number;
  paidAmount: number;
  balance: number;
  dueDate: Date;
  contact: ContactReferenceModel;
  billingMethod: keyof typeof BillingMethodType;
  paymentMethod?: PaymentMethodModel;
  merchantAccount?: MerchantAccount;
  totalRefundedAmount: number;
  totalCreditAmount: number;
  pastDueTime: string;
  isPayPopUpAllowed: boolean;
  subscriptionNumber: string;
  discount: number;
  surcharge: number;
  constructor(data: Partial<InvoiceDetailModel> = {}) {
    this.id = data.id || "";
    this.createdDate = data.createdDate || new Date();
    this.status = data.status || "Open";
    this.pastDueDays = data.pastDueDays || 0;
    this.invoiceNumber = data.invoiceNumber || "";
    this.description = data.description || "";
    this.amount = data.amount || 0;
    this.paidAmount = data.paidAmount || 0;
    this.balance = data.balance || 0;
    this.dueDate = data.dueDate || new Date();
    this.contact = data.contact || new ContactReferenceModel();
    this.billingMethod = data.billingMethod || "ChargePayment";
    this.paymentMethod =
      data.paymentMethod && new PaymentMethodModel(data.paymentMethod);
    this.merchantAccount =
      data.merchantAccount && new MerchantAccount(data.merchantAccount);
    this.totalRefundedAmount = data.totalRefundedAmount || 0;
    this.totalCreditAmount = data.totalCreditAmount || 0;
    this.pastDueTime = data.pastDueTime || "";
    this.discount = data.discount || 0;
    this.surcharge = data.surcharge || 0;

    this.isPayPopUpAllowed =
      (this.status === "Open" ||
        this.status === "PastDue" ||
        this.status === InvoiceStatus.Uncollectible) &&
      this.balance > 0;
    this.subscriptionNumber = data.subscriptionNumber || "";
  }

  static deserializeAPIModel(data: IAPIInvoiceDetailModel): InvoiceDetailModel {
    const mappedData: Partial<InvoiceDetailModel> = {
      id: data.id,
      createdDate: UtilityMethods.removeZFromDateString(data.createdDate),
      status: data.status,
      pastDueDays: data.pastDueDays,
      invoiceNumber: data.invoiceNumber,
      description: data.description,
      amount: data.amount,
      paidAmount: data.totalPaid,
      balance: data.balance,
      dueDate: UtilityMethods.removeZFromDateString(data.dueDate),
      contact: ContactReferenceModel.deserializeAPIModel(data.contact),
      billingMethod: data.billingMethod,
      paymentMethod:
        data.paymentMethod &&
        PaymentMethodModel.deserializeAPIModel(data.paymentMethod),
      merchantAccount:
        data.merchantAccount &&
        MerchantAccount.deserializeAPIModel(data.merchantAccount),
      totalRefundedAmount: data.totalRefund,
      totalCreditAmount: data.totalCredit,
      pastDueTime: data.pastDueTime,
      subscriptionNumber: data.subscriptionNumber,
    };
    return new InvoiceDetailModel(mappedData);
  }
}
